/**
 * Support 3D Component
 * Renders different support shapes (cube/cylinder/sphere) with DOF indicators
 */

import React, { useMemo } from "react";
import * as THREE from "three";
import {
  SupportConfig,
  SupportPosition,
  SUPPORT_VISUAL_CONFIGS,
} from "../../types/support";
import { SupportShape } from "./SupportShapes";
import { DOFIndicator } from "./DOFArrows";

interface Support3DProps {
  support: SupportConfig;
  nodePosition: THREE.Vector3;
  visible?: boolean;
  showDOF?: boolean;
  scaleFactor?: number;
}

/**
 * Calculate support cube position based on node position and support configuration
 * Z-UP Coordinate System: X=East, Y=North, Z=Up
 */
const calculateSupportPosition = (
  nodePosition: THREE.Vector3,
  position: SupportPosition,
  size: number,
  offset: number
): THREE.Vector3 => {
  const cubePosition = nodePosition.clone();
  const halfSize = size / 2;

  switch (position) {
    case "below":
      // Z-UP: Place cube below node in -Z direction (down)
      cubePosition.z -= halfSize + offset;
      break;
    case "above":
      // Z-UP: Place cube above node in +Z direction (up)
      cubePosition.z += halfSize + offset;
      break;
    case "left":
      // Z-UP: Place cube to the left/west in -X direction
      cubePosition.x -= halfSize + offset;
      break;
    case "right":
      // Z-UP: Place cube to the right/east in +X direction
      cubePosition.x += halfSize + offset;
      break;
    case "front":
      // Z-UP: Place cube in front/north in +Y direction
      cubePosition.y += halfSize + offset;
      break;
    case "back":
      // Z-UP: Place cube behind/south in -Y direction
      cubePosition.y -= halfSize + offset;
      break;
  }

  return cubePosition;
};

export const Support3D: React.FC<Support3DProps> = ({
  support,
  nodePosition,
  visible = true,
  showDOF = true,
  scaleFactor = 1,
}) => {
  // Use scaleFactor directly as it now comes from getSupportVisualScale
  // which provides pre-calculated fixed values from SCALE_RANGES
  const scaledSize = support.size * scaleFactor;
  const scaledOffset = support.offset * scaleFactor;
  
  // Calculate support position based on support configuration
  const supportPosition = useMemo(() => {
    return calculateSupportPosition(
      nodePosition,
      support.position,
      scaledSize,
      scaledOffset
    );
  }, [nodePosition, support.position, scaledSize, scaledOffset]);

  // Get visual config for support type
  const visualConfig = SUPPORT_VISUAL_CONFIGS[support.type];
  const supportColor = support.color || visualConfig.color;

  if (!visible) return null;

  return (
    <group position={supportPosition}>
      {/* Support shape based on type */}
      <SupportShape
        type={support.type}
        size={scaledSize}
        color={supportColor}
        opacity={support.opacity}
        visible={visible}
      />

      {/* DOF indicators */}
      {showDOF && (
        <DOFIndicator
          position={new THREE.Vector3(0, 0, 0)}
          dof={support.dof}
          supportSize={scaledSize}
          supportColor={supportColor}
        />
      )}
    </group>
  );
};

// Keep backward compatibility
export const SupportCube3D = Support3D;

/**
 * Support Collection Component
 * Renders multiple supports with different shapes and DOF indicators
 */
interface SupportCollectionProps {
  supports: SupportConfig[];
  nodePositions: Map<string, THREE.Vector3>;
  visible?: boolean;
  showDOF?: boolean;
  scaleFactor?: number;
}

export const SupportCollection: React.FC<SupportCollectionProps> = ({
  supports,
  nodePositions,
  visible = true,
  showDOF = true,
  scaleFactor = 1,
}) => {
  if (!visible) return null;

  // Ensure supports is an array
  if (!Array.isArray(supports)) {
    console.warn("⚠️ supports is not an array");
    return null;
  }

  return (
    <group>
      {supports.flatMap((support) => {
        // Ensure nodeIds exists and is an array
        if (!support) {
          console.warn("⚠️ Invalid support or nodeIds:", support);
          return []; // Return empty array for flatMap
        }

        // const nodeIds = support.nodeIds.map(String);
        let nodeIds: string[];

        // Cast to any to access old 'nodeId' property that doesn't exist in current type
        const legacySupport = support as any;

        if (Array.isArray(support.nodeIds) && support.nodeIds.length > 0) {
          // New format: Already an array with values
          nodeIds = support.nodeIds.map(String);
        } else if (legacySupport.nodeId && legacySupport.nodeId !== "") {
          // Old format: Convert single nodeId to array (non-empty)
          nodeIds = [String(legacySupport.nodeId)];
        } else {
          // Invalid: Empty or missing nodeId/nodeIds - skip silently
          // (These are orphaned supports from deleted nodes)
          return [];
        }

        return nodeIds.map((nodeId) => {
          const nodePosition = nodePositions.get(nodeId);
          if (!nodePosition) return null;

          return (
            <Support3D
              key={`${support.id}-${nodeId}`}
              support={{
                ...support,
                nodeIds, // normalized
              }}
              nodePosition={nodePosition}
              visible={visible}
              showDOF={showDOF}
              scaleFactor={scaleFactor}
            />
          );
        });
      })}
    </group>
  );
};

// Keep backward compatibility
export const SupportCubeCollection = SupportCollection;
